import { AxiosError, AxiosRequestConfig } from 'axios';
import { plainToClass } from 'class-transformer';
import dayjs from 'dayjs';
import { produce } from 'immer';
import Cookies from 'js-cookie';
import jwt_decode from 'jwt-decode';
import * as lscache from 'lscache';

import { GOOGLE_INFO } from '../common/const';
import {
  isDev,
  isLocal,
  PLANNER_URL,
  REACT_APP_RELEASE_STAGE,
  SEGP_UI_BACKEND_URL_PROD,
  SEGP_UI_BACKEND_URL_STG,
  TRANSMITTER_URL,
} from '../common/environment';
import IExperimentFilters from '../common/ExperimentFilters/ExperimentFiltersDock/IExperimentFilters';
import {
  getSource,
  getTenantSeqNumber,
  isSSOLoginTenant,
} from '../common/Tenants';
import { VariableState } from '../constants';
import { BasicUser } from '../services/CacheService';
import { trackError } from '../services/configureAnalytics';
import { FormatErrorMessageV2 } from '../services/Format';
import { pageToCollectionParams } from '../store/helpers';
import Service, {
  BackendService,
  City,
  Constraint,
  Country,
  DefaultHttpService,
  Experiment,
  ExperimentMonitorInfo,
  ExperimentReview,
  Facet,
  Group,
  LoginResponse,
  NextSchedule,
  PaginatedResponse,
  ProductConfigurationsRequest,
  Rollout,
  RolloutDiff,
  RolloutDiffCreateRequest,
  SampleSizeEstimationRequest,
  Script,
  ServiceItem,
  Strategy,
  Tag,
  TechFamily,
  TechFamilyItem,
  UpdateStateResponse,
  Vehicle,
} from './App';
import {
  AdaptiveExperiment,
  Blackday,
  BlackdayResponse,
  DomainMetadata,
  ExperimentAnalysis,
  ExperimentMetadata,
  ExperimentViews,
  getTechFamilyApproversRequest,
  Policy,
  ProductEntity,
  TFRole,
  UserRoleScopeMapping,
  VariableInfo,
  Variables,
} from './PlannerDTOs';
import { getCountryCodeByName } from '@/common/CountryCodesMap';
import { SystemEnvironment } from '@/constants/systemEnviroment';
import { globaAppContextState } from '@/contexts/app-context';
import { getExperimentDtoToExperiment } from '@/features/experiment/api';
import {
  DimensionFilterValidationStatus,
  DimensionFilterValueType,
} from '@/features/experiment/constants/analysis';
import {
  ControlMetricCandidatesResponse,
  PrepopulateFiltersRequest,
  PrepopulateFiltersResponse,
} from '@/features/experiment/detail/metrics/controlled-metrics/types';
import {
  GetExperiment,
  RegionalExperimentConfigValues,
} from '@/features/experiment/domain';
import { isCanaryEligible } from '@/features/variable/settings/canary/eligibility';
import { JoinStrategy } from '@/pages/ExperimentFormV2/Sections/Metrics/MetricRow';
import { normalizeLegacyRoleName } from '@/utils/accessControl/roles';
import { ConcedoToken } from '@/utils/concedoAuthHelper';
import { getToken, removeToken, setToken } from '@/utils/localStorage';
import { error } from '@/utils/logging';
import { getSystemEnvironment } from '@/utils/system-environment';
import { buildUrlWithParams, toQueryString } from '@/utils/url';

const ALL_EXP_STATES = [
  'UNKNOWN',
  'REQUESTED',
  'SCHEDULED',
  'STARTED',
  'PAUSED',
  'COMPLETED',
  'ABORTED',
  'CANCELLED',
];

type DimensionFilterValidationResponse = {
  status?: DimensionFilterValidationStatus;
  type?: DimensionFilterValueType;
  verified_values?: string[];
  unverified_values?: string[];
};

export const CONCEDO_IDC_ISSUER = [
  'https://concedo-idc.stg-myteksi.com',
  'https://concedo-idc.myteksi.net',
  'https://api.stg-myteksi.com/concedoidc',
  'https://api.grab.com/concedoidc',
];

export const CONCEDO_CUSTOM_HEADER = 'X-Concedo-Client-Id';
export const SYSTEM_ENVIRONMENT_HEADER = 'X-GrabX-UI';
export const SYSTEM_RELEASE_STAGE_HEADER = 'X-GrabX-Release-Stage';
export const TENANT_SEQ_NUMBER_HEADER = 'X-Tenant-Seq-Number';
export function extractCityId(cityName: string) {
  return Service.cache
    .getAllCities()
    .filter((city) => city.name === cityName)?.[0]?.id;
}
export interface TransmitterRequest {
  [key: string]: string | any;
}

export declare type ClassType<T> = new (...args: any[]) => T;

export interface TransmitterResponse {
  [key: string]: TransmitterResponseObject;
}

export interface TransmitterResponseObject {
  value: string;
  string?: string;
  resolvedBy?: string;
  resolvedWith?: string;
  variantID?: string;
}

export interface VariableComplexityPolicyPayload {
  target_cardinality_threshold: number;
  value_size_threshold: number;
  value_keys_threshold: number;
  value_depth_threshold: number;
  constraint_operators_threshold: number;
  constraint_groups_threshold: number;
  change_frequency_threshold: number;
}

/**
 * Tenant Sync - Individual rollout target in sync preview
 */
export interface TenantSyncRolloutTarget {
  id: string;
  value: unknown;
  string: string;
  constraints: Constraint[];
  excluded: boolean;
}

/**
 * Tenant Sync - Preview response from POST /v2/rollouts/{name}/syncpreview
 */
export interface TenantSyncPreviewResponse {
  // URL to existing pending rollout in OVO (if any)
  destinationPendingRolloutUrl: string | null;
  // True if OVO variable exists but is not linked to Grab
  destinationVariableNotLinked: boolean;
  // Reason why targets are excluded (e.g., "Contains Grab user specific facets...")
  exclusionReason: string;
  // The rollout data with excluded flag per target
  rollout: {
    variable: string;
    rollouts: TenantSyncRolloutTarget[];
    default: {
      value: unknown;
      string: string;
      policy?: Record<string, unknown>;
      type?: string;
      id?: string;
    };
  };
}

/**
 * Tenant Sync - Response from POST /v2/rollouts/{name}/sync
 */
export interface TenantSyncResponse {
  message: string;
  pendingReviewUrl?: string;
}

const LOGOUT_URL = '/v1/users/logout/';
const LOGIN_URL = '/v1/users/login/';
const USER_ROLES_URL = '/v2/user-roles';

/** Represents a planner service ajax api. */
export class PlannerService {
  /** The endpoint for the service. */
  private endpoint: string = PLANNER_URL;

  private token: string;

  private httpService: DefaultHttpService;

  private concedoHeaderInterceptor: number;

  constructor(HttpService) {
    this.httpService = HttpService;
    this.setupLoggingInterceptors();

    this.httpService.addInterceptor(
      (response) => response,
      (error) => {
        const r = error.response || {
          status: undefined,
          data: undefined,
          statusText: undefined,
          config: { url: '' },
        };

        const requestUrl = r?.config?.url ?? '';
        if (
          r &&
          r.status === 401 &&
          !(
            requestUrl.includes(LOGIN_URL) ||
            requestUrl.includes(LOGOUT_URL) ||
            requestUrl.includes(USER_ROLES_URL)
          )
        ) {
          this.logout();
        }

        const data = r && r.data ? `${r.data.arg} ${r.data.devMessage}` : '';
        trackError(
          [
            error.config.method,
            error.config.url,
            data,
            r.status,
            r.statusText,
          ].join(' '),
        );

        return Promise.reject(error);
      },
    );

    this.addSystemEnvironmentInterceptor();
    this.addTenantSeqNumberInterceptor();

    this.token = getToken();

    // Backward compatibility for cookie token - to remove after 27/04/2025
    const handleCookieToken = () => {
      const cookieToken = Cookies.get('session');
      if (!cookieToken) {
        return;
      }

      if (this.token || !cookieToken.includes('Bearer')) {
        Cookies.remove('session');
        return;
      }

      try {
        // cookieToken is in the format of "Bearer <token>"
        const token = cookieToken.split(' ')[1];
        const decodedInfo = jwt_decode(token);

        this.token = token;
        setToken(token, (decodedInfo as { exp: number }).exp);
        Service.http!.authorization(token);
      } catch (e) {
        error('Error parsing cookie token', e);
      } finally {
        Cookies.remove('session');
      }
    };
    handleCookieToken();

    // Add Custom Header for concedo token for BE to differentiate between tenants
    if (this.token) {
      let concedoUserInfo: ConcedoToken | null = null;

      try {
        concedoUserInfo = jwt_decode(this.token);
      } catch (e) {
        error('JWT decode error', e);
        this.token = undefined;
        removeToken();
      }

      if (!this.token) {
        return;
      }

      if (CONCEDO_IDC_ISSUER.includes(concedoUserInfo.iss)) {
        this.concedoHeaderInterceptor = this.addConcedoInterceptor(
          concedoUserInfo.aud,
        );
      }

      if (!isSSOLoginTenant()) {
        if (!concedoUserInfo.email) {
          this.token = undefined;
          removeToken();
          return;
        }

        Service.cache.setUserInfo(concedoUserInfo);
      }
    }

    this.httpService.authorization(this.token);
  }

  isLoggedIn = (): boolean => {
    if (isSSOLoginTenant()) {
      return !!this.token;
    }

    return !!this.token && !!Service.cache.getUserInfo()?.email;
  };

  login = async (params: {
    googleToken: string;
    email: string;
    name: string;
    image: string;
    source: string;
  }): Promise<void> => {
    const googleToken = params.googleToken;
    try {
      const { data } = await Service.http!.post(
        `${this.endpoint}/v1/users/login/`,
        { token: googleToken, source: params.source },
      );
      const { token, expiredAt } = data as LoginResponse;

      lscache.set(GOOGLE_INFO, params);

      this.token = token;
      setToken(token, expiredAt);
      Service.http!.authorization(token);
    } catch (ignoredError) {
      removeToken();
    }
  };

  loginConcedo = async (params: {
    code: string;
    codeVerifier: string;
    redirectURI: string;
    clientID: string;
  }): Promise<string> => {
    try {
      const { data } = await Service.http.axios.post(
        `${this.endpoint}/v1/users/login/`,
        {
          token: params.code,
          codeVerifier: params.codeVerifier,
          redirectUri: params.redirectURI,
          source: '',
        },
        {
          headers: {
            [CONCEDO_CUSTOM_HEADER]: params.clientID,
          },
        },
      );
      const { token, expiredAt } = data as LoginResponse;
      const concedoUserInfo: ConcedoToken = jwt_decode(token);

      Service.cache.setUserInfo(concedoUserInfo);

      this.token = token;
      setToken(this.token, expiredAt);
      Service.http!.authorization(this.token);
      // We need to add custom header for BE to differentiate between tenants
      // X-Concedo-Client-Id: <clientID>
      this.concedoHeaderInterceptor = this.addConcedoInterceptor(
        concedoUserInfo.aud,
      );
      return token;
    } catch (ignoredError) {
      removeToken();
    }
  };

  addConcedoInterceptor = (clientID: string): number => {
    return this.httpService.addRequestInterceptor(
      (config: AxiosRequestConfig): AxiosRequestConfig => {
        config.headers[CONCEDO_CUSTOM_HEADER] = `${clientID}`;
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        console.error(`[request error] [${JSON.stringify(error)}]`);
        return Promise.reject(error);
      },
    );
  };

  addSystemEnvironmentInterceptor = (): number => {
    return this.httpService.addRequestInterceptor(
      (config: AxiosRequestConfig): AxiosRequestConfig => {
        if (!config.headers[SYSTEM_ENVIRONMENT_HEADER]) {
          config.headers[SYSTEM_ENVIRONMENT_HEADER] =
            globaAppContextState.environment;
        }
        if (
          !config.headers[SYSTEM_RELEASE_STAGE_HEADER] &&
          REACT_APP_RELEASE_STAGE
        ) {
          config.headers[SYSTEM_RELEASE_STAGE_HEADER] = REACT_APP_RELEASE_STAGE;
        }

        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        console.error(`[request error] [${JSON.stringify(error)}]`);
        return Promise.reject(error);
      },
    );
  };

  addTenantSeqNumberInterceptor = (): number => {
    return this.httpService.addRequestInterceptor(
      (config: AxiosRequestConfig): AxiosRequestConfig => {
        if (!config.headers[TENANT_SEQ_NUMBER_HEADER]) {
          config.headers[TENANT_SEQ_NUMBER_HEADER] = getTenantSeqNumber();
        }
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        console.error(`[request error] [${JSON.stringify(error)}]`);
        return Promise.reject(error);
      },
    );
  };

  logout = async () => {
    const concedoUserInfo = Service.cache.getUserInfo();

    if (
      concedoUserInfo?.['iss'] &&
      CONCEDO_IDC_ISSUER.includes(concedoUserInfo['iss'])
    ) {
      this.logoutConcedo();
      return;
    }

    try {
      await Service.http!.post(`${this.endpoint}${LOGOUT_URL}`, {
        token: this.token,
        source: getSource(),
      });
    } catch (e) {
      console.error('error happened on logout', e);
    }

    this.token = undefined;
    removeToken();

    sessionStorage.clear();

    lscache.remove('googleInfo');
    this.logoutSubscribers.forEach((fn) => fn());
  };

  logoutConcedo = async () => {
    //Concedo IDC no logout API, so just remove token and clear.
    this.token = undefined;
    this.httpService.removeRequestInterceptor(this.concedoHeaderInterceptor);
    removeToken();

    sessionStorage.clear();

    Service.cache.setUserInfo(null);
    this.logoutSubscribers.forEach((fn) => fn());
  };

  logoutSubscribers: (() => void)[] = [];

  subscribeLogout(fn: () => void) {
    this.logoutSubscribers.push(fn);
  }

  unsubscribeLogout(fn: () => void) {
    this.logoutSubscribers.splice(this.logoutSubscribers.indexOf(fn), 1);
  }

  /** Creates an experiment. */
  createExperiment = async (payload: {
    experiment: Experiment;
    analysis_v2?: any;
  }): Promise<boolean> => {
    try {
      await Service.http!.post(`${this.endpoint}/v1/experiments/`, {
        ...payload,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
    return true;
  };

  /** Edit an experiment. */
  editExperiment = async (payload: {
    experiment: Experiment;
    analysis_v2?: any;
  }): Promise<boolean> => {
    try {
      await Service.http!.put(
        `${this.endpoint}/v1/experiments/${payload.experiment.name}/`,
        {
          ...payload,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
    return true;
  };

  editExperimentMetadata = async (payload: {
    name: string;
    metadata: ExperimentMetadata;
  }): Promise<boolean> => {
    try {
      await Service.http!.put(
        `${this.endpoint}/v2/experiments/${payload.name}/metadata`,
        {
          ...payload.metadata,
        },
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
    return true;
  };

  /** Creates experiment tags. */
  createExperimentTags = async (payload: {
    experimentName: string;
    tags: string[];
  }): Promise<boolean> => {
    try {
      await Service.http!.post(`${this.endpoint}/v1/experiment-tags/sync/`, {
        ...payload,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
    return true;
  };

  /** Creates an experiment. */
  createDraftExperiment = async (payload: {
    experiment: Experiment;
    analysis_v2?: any;
  }): Promise<boolean> => {
    try {
      await Service.http!.post(`${this.endpoint}/v1/experiments/draft/`, {
        ...payload,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
    return true;
  };

  /** Reviews an experiment. */
  reviewExperiment = async (
    experiment: Experiment,
    analysis_v2?: ExperimentAnalysis,
    regional_experiment_config_values?: RegionalExperimentConfigValues[],
  ): Promise<ExperimentReview> => {
    const r = await Service.http!.post(
      `${this.endpoint}/v1/experiments/review/`,
      { experiment, analysis_v2, regional_experiment_config_values },
    );
    return plainToClass(ExperimentReview, r.data as ExperimentReview);
  };

  reviewRollout = async (rollout: Rollout) => {
    const r = await Service.http!.post(
      `${this.endpoint}/v2/rollouts/review/?includeViolations=true`,
      {
        rollout,
      },
    );
    return r;
  };

  /** Fetches the facets from the planner service */
  fetchFacets = (env: SystemEnvironment): Promise<Facet[]> =>
    this.fetchArrayOf<Facet>(Facet, '/v1/metadata/facets/', 'meta', env);

  /** Fetches the strategies from the planner service */
  fetchStrategies = (env: SystemEnvironment): Promise<Strategy[]> =>
    this.fetchArrayOf<Strategy>(
      Strategy,
      '/v1/metadata/strategies/',
      'meta',
      env,
    );

  /** Fetches the experimentView for a particular domain. */
  fetchExperimentViews = async (
    filters: IExperimentFilters,
    limit: number = 0,
    offset: number = 0,
  ): Promise<ExperimentViews> => {
    const formatDate = (d) => {
      return dayjs(d).format('YYYY-MM-DD');
    };
    const params = {
      experimentType: filters.experimentType ? filters.experimentType : '',
      ...filters,
      ...(filters.dates
        ? {
            start: formatDate(filters?.dates[0]),
            end: formatDate(filters?.dates[1]),
          }
        : {}),
      state: filters.state ? filters.state : ALL_EXP_STATES,
      country: filters.country ? getCountryCodeByName(filters.country) : null,
      city: filters.city ? extractCityId(filters.city) : null,
      backend: filters.backend ? filters.backend.toLowerCase() : null,
      limit,
      offset,
    };

    try {
      const response = await Service.http.get(
        `${this.endpoint}/v2/experiments/${toQueryString(params)}`,
      );
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  fetchExperimentViewsV2 = async (
    filters: IExperimentFilters,
    limit: number = 0,
    offset: number = 0,
  ): Promise<ExperimentViews> => {
    const formatDate = (d) => {
      return dayjs(d).format('YYYY-MM-DD');
    };
    const params = {
      experimentType: filters.experimentType ? filters.experimentType : '',
      ...filters,
      state: filters.state ? filters.state : ALL_EXP_STATES,
      country: filters.country,
      city: filters.city ? extractCityId(filters.city) : null,
      backend: filters.backend ? filters.backend.toLowerCase() : null,
      limit,
      offset,
    };

    if (filters?.dates && !filters?.start) {
      const startKey = 'start';
      params[startKey] = formatDate(filters?.dates[0]);
    }

    if (filters?.dates && !filters?.end) {
      const endKey = 'end';
      params[endKey] = formatDate(filters?.dates[1]);
    }

    try {
      const response = await Service.http.get(
        `${this.endpoint}/v2/experiments/${toQueryString(params)}`,
      );
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches Analysis Config */
  fetchAnalysisConfig = async (experimentName: string): Promise<any> => {
    try {
      const response = await Service.http.get(
        `${this.endpoint}/v1/experiments/${experimentName}/analysis/`,
      );
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches Metric Dimension Values */
  fetchDimensionValues = async (
    metricName: string,
    dimensionName: string,
    params?: {
      cityIds: string | null;
      countryIds: string | null;
    },
  ): Promise<any> => {
    try {
      const { cityIds, countryIds } = params || {};
      let dimensionParams: any = {};
      if (cityIds) {
        dimensionParams.city_ids = cityIds;
      }
      if (countryIds) {
        dimensionParams.country_ids = countryIds;
      }

      const response = await Service.http.get(
        `${this.endpoint}/v2/merlion/${metricName}/dimension/${dimensionName}/values${toQueryString(dimensionParams)}`,
      );
      return response.data;
    } catch (e) {
      console.log(e);
      throw e;
    }
  };

  fetchDimensions = async (
    metricName: string,
    {
      purpose,
      strategy,
      joinStrategy,
      assignByFacet,
    }: {
      purpose: 'deepdives' | 'filters';
      strategy: string;
      joinStrategy?: JoinStrategy;
      assignByFacet?: string;
    },
  ): Promise<any> => {
    if (!purpose || !strategy) {
      return [];
    }

    try {
      const params = new URLSearchParams();
      params.append('purpose', purpose);
      params.append('strategy', strategy);
      if (joinStrategy) {
        params.append('join_strategy', joinStrategy);
      }
      if (assignByFacet) {
        params.append('assign_by', assignByFacet);
      }
      const res = await Service.http.get(
        `${this.endpoint}/v2/merlion/${metricName}/dimensions?${params.toString()}`,
      );
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  validateDimensionFilterValues = async (
    metricName: string,
    {
      metricVersion,
      dimensionName,
      operator,
      values,
    }: {
      metricVersion?: number;
      dimensionName: string;
      operator: string;
      values: string[];
    },
  ): Promise<DimensionFilterValidationResponse> => {
    const request = {
      metric: metricName,
      metric_version: metricVersion,
      dimension_name: dimensionName,
      operator,
      values,
    };

    const response = await Service.http.post(
      `${this.endpoint}/v2/merlion/${metricName}/dimension-filters/validate`,
      request,
    );
    return response.data;
  };

  /**
   * Fetches curated control metric candidates for timeslice experiments.
   */
  fetchControlMetricCandidates =
    async (): Promise<ControlMetricCandidatesResponse> => {
      const response = await Service.http.get(
        `${this.endpoint}/v2/merlion/control-metrics/candidates`,
      );
      return response.data;
    };

  /**
   * Batch prepopulate dimension filters from experiment constraints.
   * Maps experiment population constraints (city, country, svc) to MIDAS dimension filters.
   */
  prepopulateDimensionFilters = async (
    metrics: string[],
    constraints: Constraint[],
  ): Promise<PrepopulateFiltersResponse> => {
    // Only include supported facets for dimension filter prepopulation
    const SUPPORTED_FACETS = ['city', 'ctry', 'svc'];

    // Helper to ensure value is a string (backend expects string, not array)
    const serializeValue = (value: unknown): string => {
      if (Array.isArray(value)) {
        // Convert array to bracketed string format: [1,2] -> "[1,2]"
        return `[${value.join(',')}]`;
      }
      return String(value ?? '');
    };

    // Filter and transform constraints to the API format
    const requestConstraints = constraints
      .filter((c) => SUPPORTED_FACETS.includes(c.target))
      .map((c) => ({
        target: c.target,
        op: c.op,
        value: serializeValue(c.value),
      }));

    // If no supported constraints, return empty results
    if (requestConstraints.length === 0) {
      return {
        results: metrics.map((metric) => ({
          metric,
          dimension_filters: [],
          warnings: [],
        })),
      };
    }

    const request: PrepopulateFiltersRequest = {
      metrics,
      constraints: requestConstraints,
    };

    const response = await Service.http.post(
      `${this.endpoint}/v2/merlion/dimension-filters/prepopulate`,
      request,
    );
    return response.data;
  };

  /** Fetches Adaptive Experiments */
  fetchAdaptiveExperiments = async (
    filters: IExperimentFilters,
  ): Promise<AdaptiveExperiment[]> => {
    const formatDate = (d) => {
      return dayjs(d).format('YYYY-MM-DD');
    };
    const params = {
      experimentType: '',
      ...filters,
      ...(filters.dates
        ? {
            start: formatDate(filters?.dates[0]),
            end: formatDate(filters?.dates[1]),
          }
        : {}),
      state: filters.state ? filters.state : ALL_EXP_STATES,
      country: filters.country ? getCountryCodeByName(filters.country) : null,
      city: filters.city ? extractCityId(filters.city) : null,
      backend: filters.backend ? filters.backend.toLowerCase() : null,
    };

    try {
      const response = await Service.http.get(
        `${this.endpoint}/v2/adaptive/${toQueryString(params)}`,
      );
      return response.data.adaptive_experiments;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches overlapping experiments */
  fetchOverlappingExperiments = async (name: string) => {
    try {
      return (
        await Service.http.get(
          `${this.endpoint}/v1/experiments/overlaps/${name}/`,
        )
      ).data.overlaps;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches Adaptive trial experiments */
  fetchAdaptiveExperimentMetadata = async (experimentName: string) => {
    try {
      return await Service.http.get(
        `${this.endpoint}/v2/adaptive/${experimentName}/`,
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches Adaptive trial experiments */
  fetchAdaptiveExperimentTrials = async (experimentName: string) => {
    try {
      return (
        await Service.http.get(
          `${this.endpoint}/v2/adaptive/${experimentName}/trial/`,
        )
      )?.data?.trials;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  fetchAdaptiveArm = async (experimentName: string) => {
    try {
      return await Service.http.get(
        `${this.endpoint}/v2/adaptive/${experimentName}/arm/`,
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches metrics by exp IDs and collection IDs */
  fetchExpMetricByColIdBatch = async (expIds: string[], colIds: string[]) => {
    const r = await Service.http.post(
      `${this.endpoint}/v1/experiments/metrics/gateway`,
      {
        path: 'FindExpMetricsByColIdsBatch',
        payload: {
          experiment_ids: expIds,
          collection_ids: colIds,
        },
      },
    );
    return r.data?.results;
  };

  /** Fetches metrics by exp IDs and collection IDs */
  fetchMetricsGlossary = (params: { [key: string]: string }) => {
    const query = toQueryString(params);
    return Service.http.get(
      `${this.endpoint}/v2/merlion/metricglossary/${query}`,
    );
  };

  /** get a single exp metric based on exp ID and collection ID */
  getSingleMetricForExp = async (
    expId: string,
    colId: string,
    reqMetric: string,
  ) => {
    try {
      const expMetricResponse = await this.fetchExpMetricByColIdBatch(
        [expId],
        [colId],
      );
      const metricsRecevied =
        expMetricResponse[0].metrics_by_collection_id[0].metrics;
      for (const metricObj of metricsRecevied) {
        if (metricObj.metric === reqMetric) {
          return metricObj.value;
        }
      }
    } catch (e) {
      throw new Error(FormatErrorMessageV2(e));
    }
    throw new Error(`${reqMetric} not found for this experiment`);
  };

  getCountries = (env: SystemEnvironment): Promise<Country[]> =>
    this.fetchArrayOf<Country>(Country, '/v2/countries', 'countries', env);

  /** Fetches all cities from. */
  fetchCities = (env: SystemEnvironment): Promise<City[]> =>
    this.fetchArrayOf<City>(City, '/v1/cities/', 'cities', env);

  /** Gets a monitoring information of an experiment. */
  monitorExperiment = async (
    name: string,
  ): Promise<ExperimentMonitorInfo[]> => {
    return (
      await Service.http!.get(
        `${this.endpoint}/v1/experiments/${name}/monitor/`,
      )
    ).data as ExperimentMonitorInfo[];
  };

  /** Changes the experiment state. */
  changeExperimentState = async (
    expName: string,
    action: string,
    reason?: string,
    isAdaptiveExp?: boolean,
    approvalLink?: string,
  ): Promise<UpdateStateResponse> => {
    let endpoint = `${this.endpoint}/v1/experiments/${expName}/${action}/`;

    if (isAdaptiveExp)
      endpoint = `${
        this.endpoint
      }/v2/adaptive/${expName}/${action.toUpperCase()}/`;

    return (await Service.http!.put(endpoint, { reason, approvalLink }))
      .data as UpdateStateResponse;
  };

  /** Estimate Sample Size for experiment */
  estimateSampleSize = async (
    req: SampleSizeEstimationRequest,
  ): Promise<any> => {
    try {
      return await Service.http!.post(
        `${this.endpoint}/v1/experiments/sampleestimate/`,
        req,
      );
    } catch (e) {
      throw e.response.data.arg;
    }
  };

  /** Recommend Sample Size for experiment */
  recommendSampleSize = async (req): Promise<any> => {
    try {
      return await Service.http!.post(
        `${this.endpoint}/v1/experiments/samplerecommendation/`,
        req,
      );
    } catch (e) {
      throw e.response.data.arg;
    }
  };

  /** Fetches Population Size Via Query Button */
  fetchPopulationSize = async (req): Promise<any> => {
    try {
      return await Service.http!.post(
        `${this.endpoint}/v1/experiments/populationsize/`,
        req,
      );
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  fetchExperimentMetrics = async (experimentName: string): Promise<any> => {
    try {
      const r = await Service.http.post(
        `${this.endpoint}/v1/experiments/metrics/gateway`,
        {
          path: 'FindExpMetricsResultsBatch',
          payload: {
            experiment_ids: [experimentName],
          },
        },
      );
      return r.data?.results;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches the names of the groups. */
  fetchGroups = async (): Promise<Group[]> => {
    const r = await Service.http!.get(`${this.endpoint}/v1/groups/`);
    const groupNames = r.data.groups as string[];

    return groupNames
      .sort()
      .map((groupName: string): Group => new Group(groupName));
  };

  /** Fetches paginated groups. */
  fetchGroupsV2 = async (
    name?: string,
    limit?: number,
    offset?: number,
    sortBy?: string,
    facet?: string,
    type?: string,
    includeTotalCount?: boolean,
  ): Promise<PaginatedResponse<Group[]>> => {
    const url = `${this.endpoint}/v2/groups/?`;
    const nameString = name ? `&name=${name}` : '';
    const limitString = limit ? `&limit=${limit}` : '';
    const offsetString = offset ? `&offset=${offset}` : '';

    // TODO: deprecate groupType field
    const groupTypeString = facet ? `&groupType=${facet}` : '';

    const sortByString = sortBy ? `&sortBy=${sortBy}` : '';
    const facetString = facet ? `&facet=${facet}` : '';
    const typeString = type ? `&type=${type}` : '';
    const includeTotalCountString = includeTotalCount
      ? `&includeTotalCount=${includeTotalCount}`
      : '';
    try {
      const r = await Service.http.get(
        `${url}${nameString}${limitString}${offsetString}${groupTypeString}${sortByString}${facetString}${typeString}${includeTotalCountString}`,
      );

      r.data.items = r.data.items.map((item) => {
        return new Group(item.name, item.type);
      });

      return r.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches all policies. */
  fetchAllPolicies = async (): Promise<Policy[]> => {
    try {
      const url = `${this.endpoint}/v2/policies/`;
      const r = await Service.http.get(url);

      if (!r?.data?.policies) return [];

      return r.data.policies.map((policy) => {
        const { id, policy_name: policyName, policy_desc: policyDesc } = policy;
        return { id, policyName, policyDesc };
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches all user tf roles based on user's email. */
  fetchUserTFRoles = async (): Promise<TFRole[]> => {
    try {
      const url = `${this.endpoint}/v2/user-tf-roles/`;
      const r = await Service.http.get(url);
      return (r?.data?.items ?? []) as TFRole[];
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches all user role scopes based on user's email. */
  fetchUserRoles = async (): Promise<UserRoleScopeMapping[]> => {
    try {
      const url = `${this.endpoint}${USER_ROLES_URL}`;
      const r = await Service.http.get(url);
      const items = r?.data?.items ?? [];

      if (!Array.isArray(items)) return [];

      return items.map((role) => {
        const tf = role?.tf ?? '';
        const policyNames = Array.isArray(role?.policyNames)
          ? role.policyNames
          : [];

        return {
          tf,
          roleName: normalizeLegacyRoleName(role?.roleName, {
            techFamily: tf,
            policyNames,
          }),
          service: role?.service ?? '',
          policyNames,
        };
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  /** Fetches single blackday. */
  fetchBlackdayInfo = async (): Promise<BlackdayResponse> => {
    const url = `${this.endpoint}/v2/blackday?limit=1`;
    const r = await Service.http!.get(url);

    return r.data.blackday as BlackdayResponse;
  };

  fetchAllActiveBlackdays = async (): Promise<Blackday[]> => {
    const url = `${this.endpoint}/v2/blackday`;
    try {
      const r = await Service.http!.get(url);
      if (!r.data?.blackdays) {
        return [];
      }
      return r.data.blackdays as Blackday[];
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] fetchAllActiveBlackdays failed', {
        url,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
      });

      // Transform error for better handling in React Query
      if (err instanceof Error) {
        throw new Error(`Failed to fetch active blackdays: ${err.message}`);
      }
      throw new Error('Failed to fetch active blackdays');
    }
  };

  fetchUpcomingBlackday = async (): Promise<Blackday> => {
    const baseUrl = `${this.endpoint}/v2/blackday`;
    const params = { limit: '1' };
    const url = buildUrlWithParams(baseUrl, params);

    try {
      const r = await Service.http!.get(url);
      if (!r.data?.blackdays) {
        return null;
      }
      return r.data.blackdays?.[0];
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] fetchUpcomingBlackday failed', {
        url,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
      });

      // Transform error for better handling in React Query
      if (err instanceof Error) {
        throw new Error(`Failed to fetch upcoming blackday: ${err.message}`);
      }
      throw new Error('Failed to fetch upcoming blackday');
    }
  };

  /** Fetches variable stats */
  fetchStats = async (
    variableName: string,
    fromDate: string = '',
  ): Promise<any> => {
    try {
      const url = `${this.endpoint}/v2/variables/${variableName}/stats?fromDate=${fromDate}`;

      return await Service.http.get(url);
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  /** Fetches the name of the scripts. */
  fetchScripts = async (): Promise<Script[]> => {
    const r = await Service.http!.get(`${this.endpoint}/v1/scripts/`);

    const scripts = r.data.scripts as Script[];
    scripts.sort((a: Script, b: Script) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });
    return scripts;
  };

  fetchBasicUsers = (): Promise<BasicUser[]> =>
    Service.http
      .get(`${this.endpoint}/v1/users/`)
      .then(({ data: { users } }) => users);

  fetchAllVehicles = (env: SystemEnvironment): Promise<Vehicle[]> =>
    Service.http
      .get(`${this.endpoint}/v1/vehicles/?includeInactive=true`, env)
      .then(({ data: { vehicles } }) => vehicles);

  /** Fetches a group given the group name */
  fetchGroup(name: string): Promise<Group> {
    return this.fetchOne<Group>(Group, `/v1/groups/${name}/`, 'group');
  }

  /** Fetches a script given the script name */
  fetchScript(name: string): Promise<Script> {
    return Service.http
      .get(`${this.endpoint}/v1/scripts/${name}/`)
      .then((r) => r.data.script as Script);
  }

  fetchGroupNextSchedule(groupName: string): Promise<NextSchedule> {
    return this.fetchOne<NextSchedule>(
      NextSchedule,
      `/v1/groups/${groupName}/schedule/`,
    );
  }

  createGroup = async (group: Group): Promise<boolean> => {
    await Service.http!.post(`${this.endpoint}/v1/groups/`, { group });
    return true;
  };

  createScript = async (script: Script): Promise<boolean> => {
    await Service.http!.post(`${this.endpoint}/v1/scripts/`, { script });
    return true;
  };

  createTag = async (tag: Tag): Promise<boolean> => {
    await Service.http!.post(`${this.endpoint}/v2/tag/`, { tag });
    return true;
  };

  /** Update a group. */
  updateGroup = async (group: Group): Promise<boolean> => {
    await Service.http!.put(`${this.endpoint}/v1/groups/`, { group });
    return true;
  };

  /** Update a script. */
  updateScript = async (script: Script): Promise<boolean> => {
    await Service.http!.put(`${this.endpoint}/v1/scripts/`, { script });
    return true;
  };

  /** Update a tag. */
  updateTag = async (tag: Tag): Promise<boolean> => {
    await Service.http!.put(`${this.endpoint}/v2/tag/`, { tag });
    return true;
  };

  /** Tests a script/ */
  testScript = async (
    scriptName: string,
    sourceCode: string,
    inputs: object,
  ): Promise<object> => {
    const r = await Service.http!.post(`${this.endpoint}/v1/scripts/test/`, {
      scriptName,
      sourceCode,
      inputs,
    });
    return plainToClass(Object, r.data as object);
  };

  createAdminService = async (payload: {
    service: {
      prefix: string;
      title: string;
      techFamily: string;
      description: string;
    };
    tenantSeqs: number[];
  }): Promise<void> => {
    const url = `${this.endpoint}/v2/admin/services`;

    try {
      await Service.http!.post(url, payload);
    } catch (err: any) {
      error(err, '[PlannerService] createAdminService failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  /** Fetches the backend services from the planner service. */
  fetchBackendServices = (
    env: SystemEnvironment,
  ): Promise<BackendService[]> => {
    return this.fetchArrayOf<BackendService>(
      BackendService,
      '/v1/metadata/backend/',
      'meta',
      env,
    );
  };

  /** Fetches the tech family services from the planner service. */
  fetchTechFamilies = (env: SystemEnvironment): Promise<TechFamily[]> => {
    return this.fetchArrayOf<TechFamily>(
      TechFamily,
      '/v1/metadata/techfamily/',
      'meta',
      env,
    );
  };

  fetchDomainMetadata = (env: SystemEnvironment): Promise<DomainMetadata[]> => {
    return Service.http.get(`${this.endpoint}/v1/domains/`, env).then((res) => {
      if (!res?.data?.domains) return [];

      return res.data.domains;
    });
  };

  fetchVariable = async (name: string) => {
    const r = await Service.http!.get(`${this.endpoint}/v2/variables/${name}/`);
    return r.data;
  };

  getVariable = async (name: string) => {
    return await Service.http!.get(`${this.endpoint}/v2/variables/${name}/`);
  };

  fetchVariables = async ({
    name = '',
    domains = null,
  }): Promise<Variables> => {
    const collectionParams = pageToCollectionParams(0, 20);
    let params = {
      states: [VariableState.ACTIVE, VariableState.INACTIVE],
      offset: 0,
      name,
      ...collectionParams,
    };

    if (domains) params['domains'] = domains;

    try {
      const r = await Service.http!.get(
        `${this.endpoint}/v2/variables/${toQueryString(params)}`,
      );

      return r.data;
    } catch (e) {
      console.error(e);
      return e;
    }
  };

  fetchAllVariables = async (): Promise<Variables> => {
    const r = await Service.http!.get(`${this.endpoint}/v2/variables/`);
    return r.data;
  };

  getVariableComplexityServices = async (): Promise<{ services: string[] }> => {
    const url = `${this.endpoint}/v2/admin/variable-complexity/services`;

    try {
      const r = await Service.http!.get(url);
      return r.data;
    } catch (err: any) {
      error(err, '[PlannerService] getVariableComplexityServices failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  getServices = async (search?: string, limit = 20): Promise<ServiceItem[]> => {
    const params: Record<string, string | number> = { limit };
    if (search) params.search = search;
    const url = `${this.endpoint}/v2/services${toQueryString(params)}`;

    try {
      const r = await Service.http!.get(url);
      return r?.data?.services ?? [];
    } catch (err: any) {
      error(err, '[PlannerService] getServices failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  getTechFamilies = async (
    search?: string,
    limit = 20,
  ): Promise<TechFamilyItem[]> => {
    const params: Record<string, string | number> = { limit };
    if (search) params.search = search;
    const url = `${this.endpoint}/v2/tech-families${toQueryString(params)}`;

    try {
      const r = await Service.http!.get(url);
      return r?.data?.data?.tech_families ?? r?.data?.tech_families ?? [];
    } catch (err: any) {
      error(err, '[PlannerService] getTechFamilies failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  recomputeVariableComplexityThreshold = async (
    payload: VariableComplexityPolicyPayload & { skip_services: string[] },
  ): Promise<any> => {
    const url = `${this.endpoint}/v2/admin/variable-complexity/threshold`;

    try {
      const r = await Service.http!.put(url, payload);
      return r.data;
    } catch (err: any) {
      error(
        err,
        '[PlannerService] recomputeVariableComplexityThreshold failed',
        {
          url,
          errorMessage: err?.response?.data?.arg || 'Unknown error',
        },
      );
      throw err;
    }
  };

  backfillVariableComplexity = async (payload?: {
    skip_services: string[];
  }): Promise<any> => {
    const url = `${this.endpoint}/v2/admin/variable-complexity/backfill`;

    try {
      const r = await Service.http!.post(url, payload ?? {});
      return r.data;
    } catch (err: any) {
      error(err, '[PlannerService] backfillVariableComplexity failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  getVariableComplexityPolicies =
    async (): Promise<VariableComplexityPolicyPayload> => {
      const url = `${this.endpoint}/v2/admin/variable-complexity-policies/latest`;

      try {
        const r = await Service.http!.get(url);
        if (!r?.data) {
          return null;
        }
        return r.data;
      } catch (err: any) {
        error(err, '[PlannerService] getVariableComplexityPolicies failed', {
          url,
          errorMessage: err?.response?.data?.arg || 'Unknown error',
        });
        throw err;
      }
    };

  // Backward-compatible alias for typo usage.
  getVaraibleComplexityPolicies =
    async (): Promise<VariableComplexityPolicyPayload> => {
      return this.getVariableComplexityPolicies();
    };

  saveVariableComplexityPolicies = async (
    payload: VariableComplexityPolicyPayload,
  ): Promise<VariableComplexityPolicyPayload> => {
    const url = `${this.endpoint}/v2/variable-complexity-policies`;

    try {
      const r = await Service.http!.put(url, payload);
      if (!r?.data) {
        return null;
      }
      return r.data;
    } catch (err: any) {
      error(err, '[PlannerService] saveVariableComplexityPolicies failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  /** Fetches the variable types from the planner service. */
  fetchVariableTypes = async (env: SystemEnvironment): Promise<string[]> => {
    const r = await Service.http!.get(
      `${this.endpoint}/v1/metadata/variables/types/`,
      env,
    );
    return r.data.meta as string[];
  };

  fetchAllExperimentTags = async (
    env: SystemEnvironment,
  ): Promise<string[]> => {
    const r = await Service.http!.get(
      `${this.endpoint}/v1/experiment-tags/`,
      env,
    );
    return r.data.ExperimentTags as string[];
  };

  fetchExperimentTags = async (experimentName): Promise<any> => {
    const r = await Service.http.get(
      `${this.endpoint}/v1/experiment-tags/${experimentName}/`,
    );
    return r.data;
  };

  fetchRollout = async (varName: string): Promise<Rollout> => {
    const r = await Service.http!.get(
      `${this.endpoint}/v2/rollouts/${varName}/`,
    );

    return r.data.rollout as Rollout;
  };

  getRollout = async (varName: string): Promise<any> => {
    return await Service.http!.get(`${this.endpoint}/v2/rollouts/${varName}/`);
  };

  disableRollout = async (variable: string): Promise<boolean> => {
    await Service.http!.delete(`${this.endpoint}/v2/rollouts/${variable}/`);
    return true;
  };

  createRolloutDiff = async (
    varible: string,
    diff: RolloutDiffCreateRequest,
  ): Promise<RolloutDiff> => {
    const r = await Service.http!.post(
      `${this.endpoint}/v2/rollouts/${varible}/diff/`,
      {
        ...diff,
      },
    );

    return r.data.rolloutDiff as RolloutDiff;
  };

  getRolloutDiff = async (
    variable: string,
    id: string,
  ): Promise<RolloutDiff> => {
    const r = await Service.http!.get(
      `${this.endpoint}/v2/rollouts/${variable}/diff/${id}`,
    );

    return r.data.rolloutDiff as RolloutDiff;
  };

  declineRollout = async (
    variable: string,
    diffId: string,
  ): Promise<boolean> => {
    await Service.http!.put(
      `${this.endpoint}/v2/rollouts/${variable}/diff/${diffId}`,
      {
        isApproved: false,
      },
    );
    return true;
  };

  approveRollout = async (
    variable: string,
    diffId: string,
  ): Promise<{ canaryRunId?: number; message?: string }> => {
    const r = await Service.http!.put(
      `${this.endpoint}/v2/rollouts/${variable}/diff/${diffId}`,
      {
        isApproved: true,
      },
    );
    return r?.data || {};
  };

  approveDiff = async (
    variable: string,
    diffId: string,
    approvalLink: string = '',
  ): Promise<{ canaryRunId?: number; message?: string }> => {
    const r = await Service.http.put(
      `${this.endpoint}/v2/rollouts/${variable}/diff/${diffId}`,
      {
        isApproved: true,
        approvalLink,
      },
    );
    return r?.data || {};
  };

  getPendingRolloutDiffs = async (variable: string): Promise<RolloutDiff[]> => {
    const r = await Service.http!.get(
      `${this.endpoint}/v2/rollouts/${variable}/diff/?state=pending`,
    );

    return r.data.rolloutDiffs as RolloutDiff[];
  };

  /**
   * Tenant Sync - Get sync preview
   * Returns preview of what will be synced to a tenant, including scenario and filtered targets
   */
  getSyncPreview = async (
    variableName: string,
    targetTenantSeq: number,
  ): Promise<TenantSyncPreviewResponse> => {
    const r = await Service.http!.post(
      `${this.endpoint}/v2/rollouts/${variableName}/syncpreview`,
      { targetTenantSeq },
    );
    return r.data as TenantSyncPreviewResponse;
  };

  /**
   * Tenant Sync - Trigger sync to a tenant
   * Creates a pending review in the target tenant with the synced rollouts
   */
  syncRolloutToTenant = async (
    variableName: string,
    targetTenantSeq: number,
  ): Promise<TenantSyncResponse> => {
    const r = await Service.http!.post(
      `${this.endpoint}/v2/rollouts/${variableName}/sync`,
      { targetTenantSeq },
    );
    return r.data as TenantSyncResponse;
  };

  getTransmitterVariable = async <T>(varName: string): Promise<T> => {
    const data: TransmitterRequest = [
      {
        var: [varName],
      },
    ];

    const resp: { data: TransmitterResponse[] } = await Service.http!.post(
      `${TRANSMITTER_URL}/v1/x-portal/var/`,
      data,
    );

    const whitelist = JSON.parse(
      JSON.stringify(resp.data[0][varName].value),
    ) as T;

    return whitelist;
  };

  /** Helper method to fetch a single variable by name.
   *
   * @param prototype - The prototype of the class to convert to.
   * @param url The url for the fetch.
   * @param propertyKey The property of the underlying object to retrieve.
   */
  private fetchOne<T>(
    prototype: ClassType<T>,
    url: string,
    propertyKey?: string,
  ): Promise<T> {
    return Service.http!.get(`${this.endpoint}${url}`).then((r) => {
      return plainToClass(
        prototype,
        (propertyKey ? r.data[propertyKey] : r.data) as T,
      );
    });
  }

  /**
   * Helper method to fetch an array of variables.
   *
   * @param prototype The prototype of the class to convert to.
   * @param url The url for the fetch.
   * @param propertyKey The property of the underlying object to retrieve.
   */
  private fetchArrayOf = async <T>(
    prototype: ClassType<T>,
    url: string,
    propertyKey: string,
    env: SystemEnvironment,
  ): Promise<T[]> => {
    const r = await Service.http!.get(`${this.endpoint}${url}`, env);

    if (!r?.data?.[propertyKey]) return [];

    return plainToClass(prototype, r.data[propertyKey] as T[]);
  };

  getTransmitterResponse = (
    backend: string,
    data: TransmitterRequest,
  ): Promise<{ data: TransmitterResponse[] }> => {
    const env = getSystemEnvironment();
    let url = TRANSMITTER_URL;

    if ((isDev || isLocal) && env === SystemEnvironment.PRODUCTION) {
      url = 'https://x-transmitter.stg.mngd.int.engtools.net/api';
    }

    return Service.http!.post(`${url}/v1/${backend}/var/`, data);
  };

  /**
   * Fetches paginated tags
   *
   * @param name For searching based on tag values.
   * @param limit Number of items to return. Defaults to page size of 10.
   * @param offset Offset to query from. Defaults to 0.
   * @param sortBy Sorting the results.
   * @param groupType Filter types of tags based on tagKey. Can fetch scheduler or techfamily tags and may include experiment and domain tags in the future
   * @param includeTotalCount To return the total number of items in database matching search criteria.
   */
  fetchTags = async (
    name: string,
    limit?: number,
    offset?: number,
    sortBy?: string,
    groupType?: string,
    includeTotalCount?: boolean,
  ): Promise<PaginatedResponse<Tag[]>> => {
    const url = `${this.endpoint}/v2/tag/?`;
    const keyString = groupType ? `&tagKey=${groupType}` : '';
    const valString = name ? `&tagValue=${name}` : '';
    const limitString = limit ? `&limit=${limit}` : '';
    const offsetString = offset ? `&offset=${offset}` : '';
    const sortByString = sortBy ? `&sortBy=${sortBy}` : '';
    const includeTotalCountString = includeTotalCount
      ? `&includeTotalCount=${includeTotalCount}`
      : '';
    try {
      const r = await Service.http.get(
        `${url}${keyString}${valString}${limitString}${offsetString}${sortByString}${includeTotalCountString}`,
      );

      if (!r?.data?.items)
        return {
          items: [],
          limit: 0,
          offset: 0,
          total: 0,
          totalCount: 0,
        };

      r.data.items = r.data.items.map((item) => {
        return new Tag(item.id, item.key, item.value);
      });
      r.data.totalCount = r.data.totalNumOfTags;
      return r.data;
    } catch (e) {
      console.error(e);
      return {
        items: [],
        limit: 0,
        offset: 0,
        total: 0,
        totalCount: 0,
      };
    }
  };

  _transformVariableFilters = (data: {
    variable_filters: ProductConfigurationsRequest['variable_filters'];
  }) => {
    return produce(data, (draft) => {
      const subscribedServices = draft.variable_filters?.subscribedServices;

      if (subscribedServices) {
        draft.variable_filters.subscribedServices = [subscribedServices];
      }

      if (!draft.variable_filters) return;

      const filters = draft.variable_filters ?? {};

      if (!filters.complexityLevels) {
        delete draft.variable_filters.complexityLevels;
      } else if (!Array.isArray(filters.complexityLevels)) {
        const levels = String(filters.complexityLevels)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (levels.length === 0) {
          delete draft.variable_filters.complexityLevels;
        } else {
          draft.variable_filters.complexityLevels = levels;
        }
      } else if (
        Array.isArray(filters.complexityLevels) &&
        filters.complexityLevels.length === 0
      ) {
        delete draft.variable_filters.complexityLevels;
      }

      const guardrailViolatedKeys = Object.keys(filters).filter((k) =>
        k.startsWith('guardrailViolated_'),
      );

      if (guardrailViolatedKeys.length > 0) {
        const guardrailViolated: Record<string, boolean> = {};

        guardrailViolatedKeys.forEach((k) => {
          const subKey = k.replace('guardrailViolated_', '');
          guardrailViolated[subKey] =
            filters[k] === true || filters[k] === 'true';
          delete draft.variable_filters[k];
        });

        draft.variable_filters.guardrailViolated = guardrailViolated;
      }
    });
  };

  getProductConfigrations = (data: ProductConfigurationsRequest) => {
    return Service.http.post(
      `${PLANNER_URL}/v2/variables/search/`,
      this._transformVariableFilters(data),
    );
  };

  constraintsSearch = (data) => {
    return Service.http.post(
      `${PLANNER_URL}/v2/variables/search-constraint/`,
      this._transformVariableFilters(data),
    );
  };

  getVariableChangeLogs = (data) => {
    return Service.http.post(
      `${PLANNER_URL}/v2/variables/changelog/`,
      this._transformVariableFilters(data),
    );
  };

  getChangeLogs = (data) => {
    return Service.http.post(
      `${PLANNER_URL}/v2/variables/changelog-all/`,
      this._transformVariableFilters(data),
    );
  };

  getProductEntities = (params: string) => {
    return Service.http.get(
      `${PLANNER_URL}/v2/product-entities/search/?${params}`,
    );
  };

  createProductEntity = (data: ProductEntity) => {
    return Service.http.post(`${PLANNER_URL}/v2/product-entities/`, data);
  };

  deleteProductEntity = (name: string) => {
    return Service.http.delete(`${PLANNER_URL}/v2/product-entities/${name}/`);
  };

  archiveVariable = async (name: string) => {
    const url = `${this.endpoint}/v2/variables/${name}/archive`;

    try {
      const r = await Service.http!.post(url);
      if (!r?.data?.data) {
        return null;
      }
      return r?.data?.data;
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] archiveVariable failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      // Transform error for better handling in React Query
      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to archive variable: ${err?.response?.data?.arg}`,
        );
      }
      throw new Error('Failed to archive variable');
    }
  };

  getSegpNamespace = (env: SystemEnvironment, token: string) => {
    const url =
      env === SystemEnvironment.PRODUCTION
        ? SEGP_UI_BACKEND_URL_PROD
        : SEGP_UI_BACKEND_URL_STG;

    return fetch(`${url}/getservercapability`, {
      headers: {
        'x-xsrf-token': token,
      },
      method: 'GET',
    });
  };

  getTechFamilyApprovers = (data: getTechFamilyApproversRequest) => {
    return Service.http.post(`${PLANNER_URL}/v2/approvers`, data);
  };

  deleteVariable = async (name: string) => {
    const url = `${this.endpoint}/v2/variables/${name}/?force=true`;

    try {
      const r = await Service.http!.delete(url);
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err) {
      error(err, '[PlannerService] deleteVariable failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      // Re-throw the original error to preserve error structure for UI
      throw err;
    }
  };

  // Max threshold below the Alert state boundary (2.0). Canary engine stops
  // the rollout when a monitor's value exceeds this threshold.
  private static readonly MONITOR_ALERT_MAX_THRESHOLD = 1.5;

  private serializeCanaryForApi(variable: VariableInfo): VariableInfo {
    // canaryConfig lives inside metadata and the backend reads it from there.
    // Shape the payload according to four cases:
    //   0. Not eligible (assignBy ∉ pax/mex/dax) → strip canaryConfig entirely
    //   1. Enabled  → phases + merged metrics[], no reason/note fields
    //   2. Disabled (known reason) → enabled:false + disabledReason, no phases/metrics
    //   3. Disabled "other" → enabled:false + disabledReason + disabledReasonNote, no phases/metrics
    if (!isCanaryEligible(variable)) {
      // Strip canaryConfig — this variable type doesn't participate in canary.
      const { canaryConfig: _stripped, ...restMetadata } =
        variable?.metadata ?? ({} as any);
      return { ...variable, metadata: restMetadata };
    }

    const cfg = variable?.metadata?.canaryConfig;
    if (!cfg) return variable;

    if (!cfg.enabled) {
      // Cases 2 & 3 — strip operational fields, keep only reason metadata.
      // Rule: disabledReason must be one of the 7 accepted codes (send code, not label).
      const VALID_DISABLED_REASONS = new Set([
        'no_metrics',
        'low_risk',
        'already_full_rollout',
        'rollback',
        'urgent',
        'not_applicable',
        'other',
      ]);
      const reason =
        cfg.disabledReason && VALID_DISABLED_REASONS.has(cfg.disabledReason)
          ? cfg.disabledReason
          : undefined;
      // Rule: disabledReasonNote only when reason === 'other', non-empty, ≤ 200 chars.
      const note =
        reason === 'other' && cfg.disabledReasonNote?.trim()
          ? cfg.disabledReasonNote.trim().slice(0, 200)
          : undefined;
      const disabledConfig: Record<string, unknown> = {
        enabled: false,
        ...(reason ? { disabledReason: reason } : {}),
        ...(note ? { disabledReasonNote: note } : {}),
      };
      return {
        ...variable,
        metadata: {
          ...variable.metadata,
          canaryConfig: disabledConfig as any,
        },
      };
    }

    // Case 1 — enabled: merge query metrics and monitor rows into metrics[],
    // strip UI-only fields, no reason/note.
    const queryMetrics = ((cfg.metrics ?? []) as any[]).map(
      ({ displayName, query, min, max }) => ({
        type: 'query' as const,
        displayName,
        query,
        ...(min != null ? { min } : {}),
        ...(max != null ? { max } : {}),
      }),
    );

    // Skip monitor rows that have no URL — incomplete rows never filled in.
    const monitorMetrics = (cfg.monitors ?? [])
      .filter((m) => !!(m as any).monitorUrl)
      .map(({ displayName, monitorUrl }) => ({
        type: 'monitor' as const,
        ...(displayName ? { displayName } : {}),
        monitorUrl: monitorUrl,
        // Implicit breach threshold: canary engine stops when monitor hits
        // Alert state (2.0). Setting max to 1.5 encodes that contract.
        max: PlannerService.MONITOR_ALERT_MAX_THRESHOLD,
      }));

    return {
      ...variable,
      metadata: {
        ...variable.metadata,
        canaryConfig: {
          enabled: true,
          phases: cfg.phases,
          metrics: [...queryMetrics, ...monitorMetrics],
        },
      },
    };
  }

  updateVariable = async (variable: VariableInfo) => {
    const url = `${this.endpoint}/v2/variables/`;

    try {
      const r = await Service.http!.put(
        url,
        this.serializeCanaryForApi(variable),
      );
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] updateVariable failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      // Re-throw the original error to preserve error structure for UI
      throw err;
    }
  };

  createVariable = async (variable: VariableInfo) => {
    const url = `${this.endpoint}/v2/variables/`;

    try {
      const r = await Service.http!.post(
        url,
        this.serializeCanaryForApi(variable),
      );
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err: any) {
      // Log error for monitoring
      error(err, '[PlannerService] createVariable failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      // Re-throw the original error to preserve error structure for UI
      throw err;
    }
  };

  getExperiment = async (experimentName: string): Promise<GetExperiment> => {
    const url = `${this.endpoint}/v1/experiments/${experimentName}/`;

    try {
      const r = await Service.http!.get(url);

      return getExperimentDtoToExperiment(r?.data);
    } catch (err: any) {
      // Log error for monitoring
      error(err, '[PlannerService] getExperiment failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(`Failed to get experiment: ${err.response.data.arg}`);
      }
      throw new Error('Failed to get experiment');
    }
  };

  getRegionalExperimentChildren = async (
    experimentName: string,
    countryCode?: string,
  ): Promise<GetExperiment> => {
    const url = `${this.endpoint}/v2/experiments/${experimentName}`;

    try {
      const r = await Service.http!.post(url, { countryCode });

      return r?.data?.experiments || [];
    } catch (err: any) {
      // Log error for monitoring
      error(err, '[PlannerService] getRegionalExperimentChildren failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to get regional experiment children: ${err.response.data.arg}`,
        );
      }
      throw new Error('Failed to get regional experiment children');
    }
  };

  getAdaptiveExperiment = async (experimentName: string) => {
    const url = `${this.endpoint}/v2/adaptive/${experimentName}/`;

    try {
      const r = await Service.http!.get(url);
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err: any) {
      // Log error for monitoring
      error(err, '[PlannerService] getAdaptiveExperiment failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to get adaptive experiment: ${err.response.data.arg}`,
        );
      }
      throw new Error('Failed to get adaptive experiment');
    }
  };

  /** Changes the experiment state. */
  changeExperimentStateV2 = async ({
    expName,
    action,
    reason,
    isAdaptiveExp,
    approvalLink,
  }: {
    expName: string;
    action: string;
    reason?: string;
    isAdaptiveExp?: boolean;
    approvalLink?: string;
  }): Promise<UpdateStateResponse> => {
    let url = `${this.endpoint}/v1/experiments/${expName}/${action}/`;

    if (isAdaptiveExp)
      url = `${this.endpoint}/v2/adaptive/${expName}/${action.toUpperCase()}/`;

    try {
      const r = await Service.http!.put(url, { reason, approvalLink });
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] changeExperimentStateV2 failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to update experiment: ${err.response.data.arg}`,
        );
      }
      throw new Error('Failed to update experiment');
    }
  };

  changeRegionalExperimentState = async ({
    expName,
    countryCode,
    action,
    reason,
    isAdaptiveExp,
    approvalLink,
  }: {
    expName: string;
    countryCode?: string;
    action: string;
    reason?: string;
    isAdaptiveExp?: boolean;
    approvalLink?: string;
  }): Promise<UpdateStateResponse> => {
    let url = `${this.endpoint}/v2/experiments/${expName}/${action}`;

    if (isAdaptiveExp)
      url = `${this.endpoint}/v2/adaptive/${expName}/${action.toUpperCase()}`;

    try {
      const r = await Service.http!.put(url, {
        reason,
        approvalLink,
        countryCode,
      });
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err) {
      // Log error for monitoring
      error(err, '[PlannerService] changeRegionalExperimentState failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to update experiment: ${err.response.data.arg}`,
        );
      }
      throw new Error('Failed to update experiment');
    }
  };

  getExperimentAvailability = async (
    domain: string,
    restrictBy: string,
    isOverlap: boolean,
    isExpTimeSliceOrGroupStrategy: boolean,
    version?: number,
    experimentName?: string,
  ) => {
    if (!domain || !restrictBy) {
      return null;
    }

    const MAX_TIME_WINDOW_IN_DAYS = 90;
    const now = dayjs();
    const start = now.format('YYYY-MM-DD');
    const end = now.add(MAX_TIME_WINDOW_IN_DAYS, 'days').format('YYYY-MM-DD');
    const offsetInSec = now.utcOffset() * 60;
    experimentName = experimentName ? experimentName : '';
    version = version ? version : 1;

    const params = {
      start,
      end,
      offsetInSec: offsetInSec.toString(),
      includePartialFlag: true.toString(),
      experimentName,
      version: version.toString(),
      isOverlap: isOverlap.toString(),
      isExpTimeSliceOrGroupStrategy: isExpTimeSliceOrGroupStrategy.toString(),
    };

    const url = `${this.endpoint}/v1/experiments/availability/${domain}/${restrictBy}/`;

    const urlParams = new URLSearchParams(params);

    try {
      const r = await Service.http!.get(`${url}?${urlParams.toString()}`);
      if (!r?.data) {
        return null;
      }
      return r?.data;
    } catch (err: any) {
      // Log error for monitoring
      error(err, '[PlannerService] getExperimentAvailability failed', {
        url,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });

      if (err?.response?.data?.arg) {
        throw new Error(
          `Failed to get experiment availability: ${err.response.data.arg}`,
        );
      }
      throw new Error('Failed to get experiment availability');
    }
  };

  getScorecard = async (experimentName: string) => {
    try {
      const response = await Service.http.get(
        `${this.endpoint}/v2/experiments/${experimentName}/scorecard/`,
      );
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  getMetricScorecard = async (experimentName: string, metricId: string) => {
    try {
      const response = await Service.http.get(
        `${this.endpoint}/v2/experiments/${experimentName}/metrics/${metricId}/scorecard/`,
      );
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  getMetricQuery = async (
    experimentName: string,
    metricId: string,
    dimension?: string,
  ) => {
    try {
      const url = dimension
        ? `${this.endpoint}/v2/experiments/${experimentName}/metrics/${metricId}/query/?dimension=${encodeURIComponent(dimension)}`
        : `${this.endpoint}/v2/experiments/${experimentName}/metrics/${metricId}/query/`;

      const response = await Service.http.get(url);
      return response.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  downloadMetricQuery = async (
    experimentName: string,
    metricId: string,
    dimension?: string,
  ): Promise<void> => {
    const url = dimension
      ? `${this.endpoint}/v2/experiments/${experimentName}/metrics/${metricId}/query/?dimension=${encodeURIComponent(dimension)}`
      : `${this.endpoint}/v2/experiments/${experimentName}/metrics/${metricId}/query/`;

    const response = await this.httpService.axios.get(url, {
      headers: { Accept: 'application/sql' },
      responseType: 'blob',
    });

    const disposition: string = response.headers['content-disposition'] ?? '';
    const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = filenameMatch
      ? filenameMatch[1]
      : `axa_query_${experimentName}_${metricId}${dimension ? `_${dimension}` : '_overall'}.sql`;

    const objectUrl = URL.createObjectURL(
      new Blob([response.data], { type: 'application/sql' }),
    );
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  };

  private maskSensitive(obj: any): any {
    if (!obj || typeof obj !== 'object') return obj;
    const c = Array.isArray(obj) ? [...obj] : { ...obj };
    for (const k in c) {
      if (typeof c[k] === 'object') c[k] = this.maskSensitive(c[k]);
      if (/token|authorization/i.test(k)) c[k] = '***MASKED***';
    }
    return c;
  }

  private setupLoggingInterceptors() {
    this.httpService.addRequestInterceptor(
      (config: AxiosRequestConfig): AxiosRequestConfig => {
        console.debug(
          `[Request] ${config.method?.toUpperCase()} ${config.url}`,
          this.maskSensitive(config),
        );
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => {
        console.debug(
          `[Request Error] ${error.message}`,
          this.maskSensitive(error),
        );
        return Promise.reject(error);
      },
    );

    this.httpService.addInterceptor(
      (response) => {
        console.debug(
          `[Response] ${response.status} ${response.config.url}`,
          this.maskSensitive(response),
        );
        return response;
      },
      (error: AxiosError): Promise<AxiosError> => {
        console.debug(
          `[Response Error] ${error.message}`,
          this.maskSensitive(error),
        );
        return Promise.reject(error);
      },
    );
  }

  // ===== CANARY ROLLOUT API METHODS =====

  /**
   * Gets the active canary run for a variable (if any).
   * Returns the full run detail with phases, or null if no active canary.
   */
  getActiveCanaryRollout = async (variableName: string): Promise<any> => {
    const url = `${this.endpoint}/v2/canary/runs?variable=${encodeURIComponent(variableName)}&status=active&limit=1`;

    try {
      const r = await Service.http!.get(url);
      const runs = r?.data?.runs;
      if (!runs || runs.length === 0) {
        return null;
      }
      // Fetch full detail with phases
      return await this.fetchCanaryRun(runs[0].id);
    } catch (err) {
      error(err, '[PlannerService] getActiveCanaryRollout failed', {
        url,
        variableName,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw err;
    }
  };

  /**
   * Fetches detailed canary run information including phases
   */
  fetchCanaryRun = async (runID: number | string): Promise<any> => {
    const url = `${this.endpoint}/v2/canary/runs/${runID}`;

    try {
      const r = await Service.http!.get(url);
      if (!r?.data) {
        return null;
      }
      return r.data;
    } catch (err) {
      error(err, '[PlannerService] fetchCanaryRun failed', {
        url,
        runID,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw new Error(
        `Failed to fetch canary run: ${err?.response?.data?.arg || 'Unknown error'}`,
      );
    }
  };

  /**
   * Lists canary runs for a variable with optional filters
   */
  listCanaryRuns = async (
    variableName: string,
    status?: string,
    limit: number = 10,
    offset: number = 0,
  ): Promise<any> => {
    const params = new URLSearchParams();
    params.append('variable', variableName);
    if (status) params.append('status', status);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    const url = `${this.endpoint}/v2/canary/runs?${params.toString()}`;

    try {
      const r = await Service.http!.get(url);
      if (!r?.data) {
        return { runs: [], total: 0, limit, offset };
      }
      return r.data;
    } catch (err) {
      error(err, '[PlannerService] listCanaryRuns failed', {
        url,
        variableName,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      return { runs: [], total: 0, limit, offset };
    }
  };

  /**
   * Makes a canary decision (promote, rollback, hold, advance, override)
   */
  makeCanaryDecision = async (
    runID: number | string,
    action: 'promote' | 'rollback' | 'hold' | 'advance' | 'override',
    reason: string = '',
    additionalMins?: number,
  ): Promise<boolean> => {
    const url = `${this.endpoint}/v2/canary/runs/${runID}/decision`;
    const body: any = { run_id: String(runID), action, reason };
    if (action === 'hold' && additionalMins) {
      body.additional_mins = additionalMins;
    }

    try {
      const r = await Service.http!.post(url, body);
      return r?.status === 200;
    } catch (err) {
      error(err, '[PlannerService] makeCanaryDecision failed', {
        url,
        runID,
        action,
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      throw new Error(
        `Failed to ${action} canary: ${err?.response?.data?.arg || 'Unknown error'}`,
      );
    }
  };

  /**
   * Triggers a manual canary monitoring check
   */
  triggerCanaryCheck = async (variableName: string): Promise<boolean> => {
    const url = `${this.endpoint}/v2/canary/check`;

    try {
      const r = await Service.http!.post(url, { variable_name: variableName });
      return r?.status === 200;
    } catch (err) {
      error(err, '[PlannerService] triggerCanaryCheck failed', {
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      return false;
    }
  };

  /**
   * Returns the list of tech families enrolled in the canary rollout pilot.
   * The frontend uses this to decide whether to show the canary config tab
   * for a given variable.
   */
  getCanaryPilotTechFamilies = async (): Promise<{
    techFamilies: string[];
    usePilotTechFamilies: boolean;
  }> => {
    const url = `${this.endpoint}/v2/canary/pilot/tech-families`;

    try {
      const r = await Service.http!.get(url);
      return {
        techFamilies: r?.data?.techFamilies ?? [],
        usePilotTechFamilies: r?.data?.usePilotTechFamilies ?? true,
      };
    } catch (err) {
      error(err, '[PlannerService] getCanaryPilotTechFamilies failed', {
        errorMessage: err?.response?.data?.arg || 'Unknown error',
      });
      return { techFamilies: [], usePilotTechFamilies: true };
    }
  };

  /**
   * Unified canary validation endpoint — POST /v2/canary/validate-query.
   *
   * All callers must supply variable_name (required by backend, no omitempty).
   *
   * - Query metric test:       { query, variable_name }
   * - Query + recommendation:  { query, variable_name, fetch_recommendation: true }
   * - Monitor validation:      { type: "monitor", variable_name, monitor_url }
   */
  validateCanaryQuery = async (params: {
    query?: string;
    variable_name: string;
    type?: 'monitor';
    monitor_url?: string;
    fetch_recommendation?: boolean;
  }): Promise<{
    valid: boolean;
    value?: number;
    error?: string;
    monitorState?: string;
    recommendation?: {
      suggestedMin: number;
      suggestedMax: number;
      confidence: 'high' | 'partial';
      datapointsAnalyzed?: number;
      windowsCovered?: string[];
    };
    recommendationWarning?: string;
  }> => {
    const url = `${this.endpoint}/v2/canary/validate-query`;
    try {
      const r = await Service.http!.post(url, params);
      return r?.data;
    } catch (err) {
      return {
        valid: false,
        error: err?.response?.data?.arg || err?.message || 'Unknown error',
      };
    }
  };
}
